import React from "react";
import { PropTypes } from "prop-types";
import { MrbCollapsiblePanel } from "mrb/components/collapsible-panel";
import { MrbButton } from "mrb/components/button";
import { observer } from "mobx-react";
import { MrbToggle } from "mrb/components/checkbox";
import { NakiScheduleDatePicker } from "../../common/components";
import { Genres, Image, ProducerLabel } from "common/components";
import { isNil } from "lodash";
import { currencyFormatter } from "mrb/common/formatting";
import { formatReleaseDate } from "common/utils";

function NakiNigunArtistPreviewAlbumTemplate({ nakiNigunArtistPreviewAlbumViewStore, t }) {
    const {
        album,
        isCollapsed,
        toggleCollapse,
        toggleIsActive,
        togglePremiumStatus,
        toggleOnByDefaultStatus,
        toggleOnByDefaultInAppStatus,
        toggleOnByDefaultInNakiTouchStatus,
        toggleIsFeatured,
        onClickEdit,
        onClickCopyLink,
        setPanelRef,
        coverImageUrl,
        isPremiumDisabled,
        isActiveStatusDisabled,
        onClickAlbumTrackList,
        toggleHlsEnabledStatus,
        showScheduledRelease,
        setScheduledRelease,
    } = nakiNigunArtistPreviewAlbumViewStore;
    const { displayMissingProducers } = nakiNigunArtistPreviewAlbumViewStore.nakiNigunArtistPreviewViewStore;

    return (
        <MrbCollapsiblePanel
            title={album.title}
            innerRef={setPanelRef}
            t={t}
            isCollapsed={isCollapsed}
            onChangeCollapse={toggleCollapse}
            headerClassNameExtend="u-cursor--pointer"
            headerRender={(props) => (
                <AlbumPanelHeader
                    {...props}
                    album={album}
                    onClickCopyLink={onClickCopyLink}
                    displayMissingProducers={displayMissingProducers}
                    isPremiumDisabled={isPremiumDisabled}
                />
            )}
        >
            {() => (
                <React.Fragment>
                    <div className="row">
                        <div className="col col-sml-12 col-huge-7 u-mar--top--sml u-mar--bottom--sml">
                            <div className="row">
                                <div className="col col-sml-12 col u-mar--bottom--sml">
                                    <div className="c-collapse-panel__body__link">
                                        <p className="c-collapse-panel__body__label">
                                            {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.FIELDS.FEED_URL")}
                                        </p>
                                        <a href={album.albumFeed.feedUrl} target="_blank" rel="noreferrer">
                                            {album.albumFeed.feedUrl}
                                        </a>
                                    </div>
                                </div>

                                <div className="col col-sml-6 col-xlrg-3 col-huge-6 u-mar--bottom--sml">
                                    <p className="c-collapse-panel__body__label">
                                        {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.FIELDS.YEAR")}
                                    </p>
                                    {formatReleaseDate(album, t) || "-"}
                                </div>

                                <div className="col col-sml-6 col-xlrg-3 col-huge-6 u-mar--bottom--sml">
                                    <p className="c-collapse-panel__body__label">
                                        {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.FIELDS.GENRES")}
                                    </p>
                                    {album.genres && album.genres.length > 0 ? <Genres genres={album.genres} /> : "-"}
                                </div>

                                <div className="col col-sml-6 col-xlrg-3 col-huge-6 u-mar--bottom--sml">
                                    <p className="u-type--sml u-type--color--opaque">
                                        {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.FIELDS.CONTENT_SORT_TYPE")}
                                    </p>
                                    <p className="u-type--base">
                                        {album.albumFeed &&
                                            album.albumFeed.contentSortType &&
                                            album.albumFeed.contentSortType.name}
                                    </p>
                                </div>

                                <div className="col col-sml-6 col-xlrg-3 col-huge-6 u-mar--bottom--sml">
                                    <p className="u-type--sml u-type--color--opaque">
                                        {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.FIELDS.PRODUCER")}
                                    </p>
                                    <div className="u-type--base">
                                        {album.producers && album.producers.length > 0
                                            ? album.producers.map((p, idx) => (
                                                  <div
                                                      key={idx}
                                                      className="u-display--flex u-display--flex--ai--center u-gap--sml"
                                                  >
                                                      <ProducerLabel
                                                          name={p.producer.name}
                                                          isUser={!isNil(p.producer.user)}
                                                          user={p.producer.user}
                                                      />
                                                      <span className="u-type--sml u-type--color--opaque">
                                                          ({p.revenuePercentage}%)
                                                      </span>
                                                  </div>
                                              ))
                                            : "-"}
                                    </div>
                                </div>

                                <div className="col col-sml-6">
                                    <div>
                                        <p className="c-collapse-panel__body__label">
                                            {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.FIELDS.COVER_IMAGE")}
                                        </p>
                                        <Image className="c-upload__uploaded-img" src={coverImageUrl} />
                                    </div>
                                    {/* <div className="row u-mar--top--sml">
                                                <PodcastGroupPreviewSubPodcastAnalyticsStats
                                                    store={subPodcastAnalyticsStatsWidgetViewStore}
                                                    t={t}
                                                />
                                            </div> */}
                                </div>
                                <div className="col col-sml-6 col-xlrg-3 col-huge-6 u-mar--bottom--sml">
                                    <p className="u-type--sml u-type--color--opaque">
                                        {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.FIELDS.PRICE")}
                                    </p>
                                    <p className="u-type--base">
                                        {album.price
                                            ? currencyFormatter.format(album.price, {
                                                  currency: "USD",
                                              })
                                            : "-"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="col col-sml-12 col-huge-5 u-mar--top--sml u-mar--bottom--sml">
                            <div className="c-toggle__card-list">
                                <div className="c-toggle__item--inline">
                                    {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.BUTTONS.STATUS")}
                                    <MrbToggle
                                        id={`album-active-status-${album.id}`}
                                        value={album.isActive}
                                        disabled={isActiveStatusDisabled}
                                        onChange={(event) => toggleIsActive(event.target.checked)}
                                    />
                                </div>
                                {showScheduledRelease && (
                                    <div className="c-schedule-release">
                                        <div className="c-schedule-release__label">Schedule release</div>
                                        <NakiScheduleDatePicker
                                            value={album.scheduledReleaseAt || null}
                                            onChange={(val) => setScheduledRelease(val || null)}
                                            showTimeSelect
                                            timeIntervals={60}
                                            onChangeRaw={(e) => e.preventDefault()}
                                            dateFormat="MM/dd/yyyy h aa"
                                            minDate={new Date()}
                                            isClearable
                                            placeholder="Pick date & time"
                                        />
                                    </div>
                                )}

                                <div className="c-toggle__item--inline">
                                    {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.BUTTONS.PREMIUM")}
                                    <MrbToggle
                                        id={`album-premium-status-${album.id}`}
                                        value={album.isPremium}
                                        disabled={isPremiumDisabled}
                                        onChange={(event) => togglePremiumStatus(event.target.checked)}
                                    />
                                </div>

                                <div className="u-separator--primary u-mar--none"></div>

                                <div className="c-toggle__item--inline">
                                    {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.BUTTONS.MAKE_DEFAULT")}
                                    <MrbToggle
                                        id={`album-on-by-default-status-${album.id}`}
                                        value={album.isTurnedOnByDefault}
                                        onChange={(event) => toggleOnByDefaultStatus(event.target.checked)}
                                    />
                                </div>
                                <div className="c-toggle__item--inline">
                                    {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.BUTTONS.MAKE_DEFAULT_IN_APP")}
                                    <MrbToggle
                                        id={`album-on-by-default-in-app-status-${album.id}`}
                                        value={album.isTurnedOnByDefaultInApp}
                                        onChange={(event) => toggleOnByDefaultInAppStatus(event.target.checked)}
                                    />
                                </div>
                                <div className="c-toggle__item--inline">
                                    {t(
                                        "ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.BUTTONS.MAKE_DEFAULT_IN_EMBEDDED_APP"
                                    )}
                                    <MrbToggle
                                        id={`album-on-by-default-in-embedded-app-status-${album.id}`}
                                        value={album.isTurnedOnByDefaultInEmbeddedApp}
                                        onChange={(event) => toggleOnByDefaultInNakiTouchStatus(event.target.checked)}
                                    />
                                </div>

                                <div className="u-separator--primary u-mar--none"></div>

                                <div className="c-toggle__item--inline">
                                    {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.BUTTONS.FEATURED")}
                                    <MrbToggle
                                        id={`album-featured-status-${album.id}`}
                                        value={album.isFeatured}
                                        onChange={(event) => toggleIsFeatured(event.target.checked)}
                                    />
                                </div>
                                <div className="c-toggle__item--inline">
                                    {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.BUTTONS.HLS_ENABLED")}
                                    <MrbToggle
                                        id={`album-hls-enabled-status-${album.id}`}
                                        value={album.albumFeed.hlsEnabled}
                                        onChange={(event) => toggleHlsEnabledStatus(event.target.checked)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="u-separator--primary"></div>
                    <MrbButton
                        onClick={onClickEdit}
                        label="ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.BUTTONS.ALBUM_EDIT"
                        t={t}
                    />{" "}
                    <MrbButton
                        onClick={onClickAlbumTrackList}
                        className="c-btn c-btn--base c-btn--ghost"
                        label="ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.BUTTONS.SONGS"
                        t={t}
                    />
                </React.Fragment>
            )}
        </MrbCollapsiblePanel>
    );
}

NakiNigunArtistPreviewAlbumTemplate.propTypes = {
    album: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

const AlbumPanelHeader = observer(function AlbumPanelHeader({
    title,
    t,
    isCollapsed,
    album,
    displayMissingProducers,
    isPremiumDisabled,
    onClickCopyLink,
}) {
    return (
        <React.Fragment>
            <div className="u-display--flex u-display--flex--ai--center">
                {t(title)}{" "}
                {album.isPremium && (
                    <i className="u-icon u-icon--med u-icon--premium u-mar--left--tny u-mar--right--tny"></i>
                )}
            </div>
            <div className="u-display--flex u-display--flex--ai--center">
                {((isPremiumDisabled && (!album.producers || album.producers.length === 0)) ||
                    (displayMissingProducers && (!album.producers || album.producers.length === 0))) && (
                    <p className="u-type--sml u-type--color--warning--light u-mar--right--lrg">
                        {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.VALIDATION.MISSING_PRODUCER_LABEL")}
                    </p>
                )}{" "}
                <button
                    type="button"
                    className="c-btn c-btn--sml c-btn--ghost u-mar--right--sml"
                    onClick={(event) => {
                        // The header toggles the panel, so keep a copy from collapsing the card.
                        event.stopPropagation();
                        onClickCopyLink();
                    }}
                >
                    {t("ADMINISTRATION.NAKI_NIGUN.ARTIST.PREVIEW.ALBUM.BUTTONS.COPY_LINK")}
                </button>
                <i
                    className={`u-icon u-icon--med u-icon--arrow-down ${
                        isCollapsed ? "u-rotate--180" : ""
                    } u-cursor--pointer`}
                ></i>
            </div>
        </React.Fragment>
    );
});

export default observer(NakiNigunArtistPreviewAlbumTemplate);
